import {Box, IconButton, Stack, TextField, Typography,} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import React, {useEffect, useState} from "react";
import {patch} from "../../api/api";
import {showMessage} from "../../utils/snackbar";
import {ThumbDown, ThumbUp} from "@mui/icons-material";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {AIDialog} from "./AIDialog";

export interface ReportProps {
    encounterId: string;
    report: Record<string, string>;
    onAddScore: (score: number) => void;
    disabled: boolean;
    onReportChange: (report: Record<string, string>) => void;
    onModifyAI: (key: string, text: string) => void;
    /** The language code you want to use for speech‑to‑text (e.g. 'nl', 'en'). */
    language: string;
}

const ReportBox: React.FC<ReportProps> = ({
                                              encounterId,
                                              report = {},
                                              onAddScore,
                                              disabled,
                                              onReportChange,
                                              onModifyAI,
                                              language,
                                          }) => {
    const [editedReport, setEditedReport] = useState<Record<string, string>>(report);
    const [modifyAIOpen, setModifyAIOpen] = useState(false);
    const [currentAIKey, setCurrentAIKey] = useState<string | null>(null);
    const [aiText, setAIText] = useState("");

    useEffect(() => {
        setEditedReport(report);
    }, [report]);

    const handleItemCopy = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            showMessage("Gekopieerd", "success", 1000);
        } catch (error) {
            console.error("Failed to copy item:", error);
        }
    };

    const handleChange = (key: string, newValue: string) => {
        const updatedReport = {...editedReport, [key]: newValue};
        setEditedReport(updatedReport);
        onReportChange(updatedReport);
    };

    const handleBlur = async (key: string, value: string) => {
        try {
            await patch(`/encounters/${encounterId}/report`, JSON.stringify({key, value}));
        } catch (error) {
            console.warn(`Error updating field ${key}:`, error);
            showMessage("Er is een fout opgetreden bij het bijwerken van het veld", "error");
        }
    };

    return (
        <Box sx={{mt: "24px"}}>
            <Stack>
                <Stack spacing={'16px'}>
                    {Object.entries(editedReport).map(([key, value]) => (
                        <Stack key={key} spacing={"8px"}>
                            <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
                                {key}
                            </Typography>

                            <Box
                                key={key}
                                sx={{
                                    mb: "8px",
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing="8px">
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={1}
                                        value={value || ""}
                                        onChange={(e) => handleChange(key, e.target.value)}
                                        onBlur={(e) => handleBlur(key, e.target.value)}
                                        disabled={disabled}
                                    />
                                    <Stack direction="row" spacing="8px">
                                        <IconButton
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                '&:hover': {
                                                    animation: 'shake 0.5s ease-in-out ',
                                                },
                                                '@keyframes shake': {
                                                    '0%': {transform: 'rotate(0deg)'},
                                                    '25%': {transform: 'rotate(-5deg)'},
                                                    '50%': {transform: 'rotate(5deg)'},
                                                    '75%': {transform: 'rotate(-3deg)'},
                                                    '100%': {transform: 'rotate(0deg)'},
                                                }
                                            }}
                                            onClick={() => {
                                                setCurrentAIKey(key);
                                                setAIText("");
                                                setModifyAIOpen(true);
                                            }}
                                            disabled={disabled}
                                            size="small"
                                        >
                                            <AutoFixHighIcon fontSize="small"/>
                                        </IconButton>

                                        <IconButton
                                            sx={{
                                                width: 32,
                                                height: 32,
                                            }}
                                            onClick={() => handleItemCopy(value || "onbekend")}
                                            size="small"
                                        >
                                            <ContentCopyIcon fontSize="small"/>
                                        </IconButton>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Stack>
                    ))}
                </Stack>

                <AIDialog
                    open={modifyAIOpen}
                    onClose={() => setModifyAIOpen(false)}
                    onChange={setAIText}
                    onConfirm={() => {
                        if (currentAIKey) {
                            onModifyAI(currentAIKey, aiText);
                        }
                    }}
                    language={language}
                />
                <Stack direction="row" justifyContent="flex-end" alignItems="center">
                    <Typography variant="body1" sx={{m: 0}}>
                        Wat vind je van het verslag?
                    </Typography>
                    <Stack direction="row" alignItems="center">
                        <IconButton
                            color="primary"
                            onClick={() => onAddScore(1)}
                            disabled={disabled || Object.keys(editedReport).length === 0}
                        >
                            <ThumbUp/>
                        </IconButton>
                        <IconButton
                            color="primary"
                            onClick={() => onAddScore(0)}
                            disabled={disabled || Object.keys(editedReport).length === 0}
                        >
                            <ThumbDown/>
                        </IconButton>
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    );
};

export {ReportBox};
